import { usePublishedPrices } from '../../features/catalog/queries';
import { quotePrice, type PriceSpec } from '../../lib/contentPrice';
import { serializeJsonLd } from '../../lib/jsonLd';
import { faqPageSchema } from '../../lib/seo/faqPage';

/** An answer is plain text, or text with AnyAPI's price named by SKU, e.g.
 *  `["It's ", { sku: "google.search" }, " per 1,000 searches."]`. A post cannot
 *  put `<Price>` in a string, and the schema needs the answer as one string. */
type FaqAnswer = string | (string | PriceSpec)[];

export interface FaqProps {
  items: { q: string; a: FaqAnswer }[];
  /** heading text; rendered as an in-prose h2 so the TOC picks it up */
  title?: string;
}

/**
 * Renders an answer-first FAQ as a semantic definition list and emits a
 * matching `FAQPage` JSON-LD block. The schema answer text is the same string
 * shown to readers, which is what Google requires for FAQ rich results. Lives
 * in the MDX component map so any post can close with `<FAQ items={[...]} />`.
 */
export const FAQ = ({ items, title = 'Frequently asked questions' }: FaqProps) => {
  const prices = usePublishedPrices();
  if (!items || items.length === 0) return null;
  const entries = items.map(({ q, a }) => ({
    q,
    a: typeof a === 'string'
      ? a
      : a.map((part) => {
        if (typeof part === 'string') return part;
        return prices ? quotePrice(prices, part).text : '-';
      }).join(''),
  }));
  return (
    <section aria-labelledby="faq">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(faqPageSchema(entries)) }}
      />
      <h2 id="faq">{title}</h2>
      <dl className="not-prose">
        {entries.map((it) => (
          <div key={it.q} className="border-t border-line py-4 first:border-t-0">
            <dt className="text-18 font-semibold text-text">{it.q}</dt>
            <dd className="mt-1.5 ml-0 text-16 leading-relaxed text-text-2">{it.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
